import { useCallback, useEffect, useState } from "react";
import SunCalc from "suncalc";

export type ThemeMode = "light" | "dark" | "auto";

const STORAGE_KEY = "bowfast-theme";

// ── Helpers ───────────────────────────────────────────────────────────────────

function getStoredTheme(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "auto") return v;
  } catch {
    // localStorage blocked (private mode, permissions)
  }
  return "auto";
}

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Returns whether the current local time is after sunset or before sunrise,
 * using the browser's Geolocation API + suncalc.
 * Returns `null` when geolocation is unavailable or denied.
 * NOTE: no `timeout` option — the browser permission dialog must complete
 * before getCurrentPosition can resolve; a short timeout fires the error
 * callback while the dialog is still showing.
 */
async function isDarkByLocation(): Promise<boolean | null> {
  if (typeof navigator === "undefined" || !navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const times = SunCalc.getTimes(new Date(), coords.latitude, coords.longitude);
        const now = Date.now();
        resolve(now < times.sunrise.getTime() || now > times.sunset.getTime());
      },
      () => resolve(null),
      { maximumAge: 3600000 }, // cache position for 1 hour; no timeout
    );
  });
}

// Quick synchronous guess before async location resolves
// Uses the value computed by the blocking <script> in index.html when available,
// so React's first paint already has the correct theme class on <html>.
function resolveSync(mode: ThemeMode): boolean {
  if (mode === "dark") return true;
  if (mode === "light") return false;
  // Read the pre-computed value from the blocking script
  if (typeof window !== "undefined" && (window as any).__BOWFAST_THEME_DARK != null) {
    return (window as any).__BOWFAST_THEME_DARK as boolean;
  }
  return systemPrefersDark();
}

// ── Hook ─────────────────────────────────────────────────────────────────────

interface UseThemeReturn {
  /** The user's stored preference */
  theme: ThemeMode;
  /** The resolved dark/light state (after location lookup for auto) */
  resolvedDark: boolean;
  setTheme: (mode: ThemeMode) => void;
}

export function useTheme(): UseThemeReturn {
  const [theme, setThemeState] = useState<ThemeMode>(getStoredTheme);
  const [resolvedDark, setResolvedDark] = useState<boolean>(() => resolveSync(getStoredTheme()));

  // Apply / remove .dark on <html>
  // Only apply AFTER the AppLoader is dismissed (window.__BOWFAST_READY)
  // to prevent the async geo-resolution from flashing behind the loader.
  useEffect(() => {
    const apply = () => {
      const root = document.documentElement;
      if (resolvedDark) {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    };

    if ((window as any).__BOWFAST_READY) {
      apply();
    } else {
      // Poll briefly — the flag is set by _authenticated when the loader dismisses
      const id = setInterval(() => {
        if ((window as any).__BOWFAST_READY) {
          clearInterval(id);
          apply();
        }
      }, 100);
      return () => clearInterval(id);
    }
  }, [resolvedDark]);

  // Resolve theme whenever the preference changes
  useEffect(() => {
    if (theme === "light") {
      setResolvedDark(false);
      return;
    }
    if (theme === "dark") {
      setResolvedDark(true);
      return;
    }

    // Auto — try location, fall back to system preference
    let cancelled = false;

    isDarkByLocation().then((isDark) => {
      if (!cancelled) setResolvedDark(isDark ?? systemPrefersDark());
    });

    // Re-evaluate when geolocation permission is granted after the fact
    let permUnsubscribe: (() => void) | undefined;
    if (typeof navigator !== "undefined" && navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          const onPermChange = () => {
            if (status.state === "granted" && !cancelled) {
              isDarkByLocation().then((isDark) => {
                if (!cancelled) setResolvedDark(isDark ?? systemPrefersDark());
              });
            }
          };
          status.addEventListener("change", onPermChange);
          permUnsubscribe = () => status.removeEventListener("change", onPermChange);
        })
        .catch(() => {
          // permissions API not supported — ignore
        });
    }

    // Re-react to OS preference changes while in auto mode
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemChange = () => {
      isDarkByLocation().then((isDark) => {
        if (!cancelled) setResolvedDark(isDark ?? mq.matches);
      });
    };
    mq.addEventListener("change", onSystemChange);

    return () => {
      cancelled = true;
      mq.removeEventListener("change", onSystemChange);
      permUnsubscribe?.();
    };
  }, [theme]);

  const setTheme = useCallback((next: ThemeMode) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage blocked
    }
  }, []);

  return { theme, resolvedDark, setTheme };
}
